import { AppFactory, IApp, Device, Engine, OS } from '@onereach/apps-client-web-sdk';
import { Listeners } from '@or-sdk/hitl';

export interface InitializePushNotificationsOptions {
  signUpToNotificationsCallback(appId: string, deviceId: string): Promise<Listeners.Listener>;
  startWithNotificationCallback(context: any): void;
}

export class AppsIntegration {
  public static readonly PUSH_NOTIFICATIONS_SUPPORTED_ON = [
    OS.Android, OS.IOS, OS.Windows,
  ];

  public static handledPushNotificationIds: Set<string> = new Set();
  public static app: IApp;
  public static device: Device;

  /**
   * We should be able to initialize Apps API all the time.
   * Some feature can be available or not independently.
   */
  public static async initializeAppsWebSDK(appId: string) {
    this.app = await AppFactory.init({ id: appId });
    this.device = await this.app.getDevice();
    console.debug('[Apps] App Data', this.app, this.device);
  }

  /**
   * Try to access push notifications API.
   * If all requirements were satisfied - call callback with context that started / focused the app.
   * If something went wrong, was not allowed or not available - return false.
   */
  public static async initializePushNotifications(options: InitializePushNotificationsOptions) {
    const doesDeviceSupportApi = this.isPushNotificationsApiAvailable();
    if (!doesDeviceSupportApi) {
      return false;
    }

    const isPermissionGranted = await this.app.requestPushNotificationsPermission();
    if (!isPermissionGranted) {
      return false;
    }

    try {
      await options.signUpToNotificationsCallback(this.app.id, this.device.id);
    } catch (e) {
      console.error('[Apps] Failed to subscribe to notifications!', e);
      return false;
    }

    this.onAppLaunchOrFocus(options.startWithNotificationCallback);
    return true;
  }

  public static isPushNotificationsApiAvailable() {
    const isUsingApp = this.device.engine === Engine.OS;
    const isSupportedOS = this.PUSH_NOTIFICATIONS_SUPPORTED_ON.includes(this.device.os);
    return isUsingApp && isSupportedOS;
  }

  public static onAppLaunchOrFocus(callback: (context: any) => void) {
    this.onAppLaunch(callback);
    this.app.onfocus = () => {
      this.onAppFocus(callback);
    };
  }

  public static async onAppLaunch(callback: (context: any) => void) {
    const params = await this.app.getLaunchParameters();
    const notification = params.launchNotification;

    if (notification) {
      this.handledPushNotificationIds.add(notification.id);
      callback(notification.context);
    }
  }

  public static async onAppFocus(callback: (context: any) => void) {
    const activation = await this.app.getLastNotificationActivation();
    if (!activation) {
      return;
    }

    const { notification } = activation;
    if (!this.handledPushNotificationIds.has(notification.id)) {
      this.handledPushNotificationIds.add(notification.id);
      callback(notification.context);
    }
  }
}
